import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

function MetaTags({
  title = 'Soopra.ai – AI Agents Trained by Real Experts',
  description = 'Soopra AI, where your AI Persona becomes your digital double—smart, engaging, and endlessly available.',
  image = 'https://soopra.ai/banner.jpg',
  type = 'website',
  siteName = 'Soopra AI',
}) {
  const { pathname, search } = useLocation();
  // Get current URL safely for SSR
  const currentUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : `${process.env.REACT_APP_URL || 'https://soopra.ai'}${pathname}${search}`;
  return (
    <Helmet>
      {/* Standard metadata tags */}
      {title && <title>{title}</title>}
      {/* The <title> tag defines the title of the document shown in the browser tab and search engine results. */}

      {/* Canonical link tag: Helps prevent duplicate content issues by specifying the preferred version of a web page. */}
      <link rel='canonical' href={currentUrl} />
      {/* If no URL is explicitly passed, it's safer not to use window.location.href directly as it can cause issues in SSR environments. */}

      {description && <meta name='description' content={description} />}
      {/* The <meta name='description'> tag provides a concise summary of the page's content for search engine results. */}

      {/* Open Graph tags (OG): Used by social media platforms (like Facebook, LinkedIn) to display rich previews when a link is shared. */}

      <meta property='og:url' content={currentUrl} />
      {/* og:url: The canonical URL of your object that will be used as its permanent ID in the graph. */}

      {type && <meta property='og:type' content={type} />}
      {/* og:type: The type of object you're sharing, e.g., 'website', 'article', 'book', 'product'. */}

      {title && <meta property='og:title' content={title} />}
      {/* og:title: The title of your object as it should appear within the graph, e.g., in a news feed. */}

      {description && <meta property='og:description' content={description} />}
      {/* og:description: A one to two sentence description of your object. */}

      {siteName && <meta property='og:site_name' content={siteName} />}
      {/* og:site_name: The name of your website. */}

      {/* OG image tags */}
      {image && <meta property='og:image' content={image} />}
      {/* og:image: An image URL which should represent your object within the graph. */}

      {image && <meta property='og:image:secure_url' content={image} />}
      {/* og:image:secure_url: An alternate URL to use if the webpage is accessed over HTTPS. */}

      {image && <meta property='og:image:type' content='image/jpeg' />}
      {/* og:image:type: The MIME type of the image. Common types include image/jpeg, image/png, image/gif. */}

      {image && <meta property='og:image:width' content='1200' />}
      {/* og:image:width: The width of the image in pixels. Recommended for better rendering. */}

      {image && <meta property='og:image:height' content='630' />}
      {/* og:image:height: The height of the image in pixels. Recommended for better rendering. */}

      {image && title && (
        <meta
          property='og:image:alt'
          content={`Image for ${title} on ${siteName}`}
        />
      )}
      {/* og:image:alt: A description of what is in the image, useful for accessibility. */}

      {/* Twitter Cards tags: Used by Twitter to display rich previews for shared links. */}
      {/* A "card" is a rich media object that Twitter attaches to Tweets. */}

      {/* twitter:card: The type of Twitter card. 'summary' is a good default. Other options include 'summary_large_image', 'app', 'player'. */}
      <meta name='twitter:card' content='summary_large_image' />

      {/* twitter:site: The @username of the website. Used to attribute the content to the website and not the individual author. */}
      {siteName && (
        <meta name='twitter:site' content={`@${siteName.replace(/\s/g, '')}`} />
      )}

      {title && <meta name='twitter:title' content={title} />}
      {/* twitter:title: The title of the content as it should appear in the Twitter card. */}

      {description && <meta name='twitter:description' content={description} />}
      {/* twitter:description: A description of the content for the Twitter card, ideally 200 characters or less. */}

      {image && <meta name='twitter:image' content={image} />}
      {/* twitter:image: The URL of an image to be used in the card. Images for 'summary_large_image' should be at least 300x157px. */}
    </Helmet>
  );
}

export default MetaTags;
